import { type RefCallback, type RefObject, useCallback } from 'react'

/**
 * Merge two or more refs of the same type into a single ref.
 *
 * Referentially stable with the refs, so stable if all the refs themselves are
 * stable (use memoization).
 */
export const useMergedRefs = <T,>(...refs: Array<RefObject<T | null> | RefCallback<T> | null>): RefCallback<T> =>
	useCallback((value) => {
		const cleanups = refs.map((ref) => {
			if (typeof ref === 'function') {
				return ref(value)
			}
			if (ref) {
				ref.current = value
			}
			return undefined
		})

		// No cleanups to run, implicit null run works
		if (cleanups.every((cleanup) => cleanup === undefined)) {
			return undefined
		}

		// Run cleanup of each ref manually
		return () => {
			refs.forEach((ref, index) => {
				const cleanup = cleanups[index]
				if (cleanup) {
					cleanup()
				} else if (typeof ref === 'function') {
					ref(null)
				} else if (ref) {
					ref.current = null
				}
			})
		}

		// biome-ignore lint/correctness/useExhaustiveDependencies: The refs should be used directly as dependencies, since they'll change each call
	}, refs)
